/**
 * SocialCapitalChip — displays a writer's current Social Capital as
 * a level badge + point count + optional progress bar to next level.
 *
 * Variants:
 *   • "inline"  — one-line chip for article headers, card avatars
 *                 (icon · level · points)
 *   • "full"    — profile-page block with progress bar to next level
 *   • "compact" — icon + points only (used on card avatars)
 *
 * Uses `GET /api/publisher/v1/writers/{writerId}` when only writerId
 * is given, or accepts a pre-fetched `capital` object to avoid the
 * extra network roundtrip on lists.
 */
import { useEffect, useState } from "react";
import { Sparkles, TrendingUp } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

// Emoji-free glyph mapping keyed to level names in backend `LEVELS`.
const LEVEL_STYLES = {
  "Helper":            { color: "#64748b", bg: "#f1f5f9" },
  "Friend":            { color: "#0891b2", bg: "#ecfeff" },
  "Supporter":         { color: "#0f766e", bg: "#f0fdfa" },
  "Community Builder": { color: "#B58936", bg: "#fff8ea" },
  "Impact Leader":     { color: "#9333ea", bg: "#faf5ff" },
  "Joy Ambassador":    { color: "#db2777", bg: "#fdf2f8" },
  "Global Changemaker":{ color: "#dc2626", bg: "#fef2f2" },
};

async function fetchCapital(writerId) {
  const r = await fetch(`${BASE}/writers/${writerId}`);
  if (!r.ok) return null;
  const d = await r.json();
  return d.social_capital;
}

export default function SocialCapitalChip({
  writerId,
  capital: capitalProp,
  variant = "inline",
  className = "",
  testId = "social-capital-chip",
}) {
  const [capital, setCapital] = useState(capitalProp || null);
  useEffect(() => {
    if (capitalProp) { setCapital(capitalProp); return; }
    if (!writerId) return;
    let alive = true;
    fetchCapital(writerId).then((c) => { if (alive) setCapital(c); });
    return () => { alive = false; };
  }, [writerId, capitalProp]);

  if (!capital) return null;

  const style = LEVEL_STYLES[capital.level] || LEVEL_STYLES["Helper"];

  if (variant === "compact") {
    return (
      <span
        className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-mono ${className}`}
        style={{ color: style.color, background: style.bg }}
        title={`${capital.level} · ${capital.points.toLocaleString()} pts`}
        data-testid={testId}
      >
        <Sparkles className="w-2.5 h-2.5" />
        {capital.points.toLocaleString()}
      </span>
    );
  }

  if (variant === "full") {
    return (
      <div className={`rounded-2xl p-5 border ${className}`}
           style={{ background: style.bg, borderColor: style.color + "33" }}
           data-testid={testId}>
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4" style={{ color: style.color }} />
          <div className="text-xs uppercase tracking-widest font-bold" style={{ color: style.color }}>
            Social Capital
          </div>
        </div>
        <div className="text-2xl font-bold text-slate-900 mb-0.5" style={{ fontFamily: "'Lora', serif" }}>
          {capital.level}
        </div>
        <div className="text-sm text-slate-600 mb-3">
          {capital.points.toLocaleString()} points
        </div>
        {capital.next_level && (
          <>
            <div className="h-2 bg-white/70 rounded-full overflow-hidden">
              <div
                className="h-full transition-all"
                style={{
                  width: `${capital.progress_pct}%`,
                  background: style.color,
                }}
                data-testid={`${testId}-progress`}
              />
            </div>
            <div className="mt-2 flex items-center gap-1 text-[11px] text-slate-500">
              <TrendingUp className="w-3 h-3" />
              {capital.next_threshold - capital.points} pts to <strong>{capital.next_level}</strong>
            </div>
          </>
        )}
      </div>
    );
  }

  // "inline" default
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${className}`}
      style={{ color: style.color, background: style.bg }}
      data-testid={testId}
    >
      <Sparkles className="w-3 h-3" />
      {capital.level}
      <span className="opacity-70 font-normal">· {capital.points.toLocaleString()}</span>
    </span>
  );
}
